import { StoreModel } from '@/models/store';

export interface AtlasContext {
  packageName: string | null;
  /**
   * Has the store's FIRST Shopify import finished? Atlas needs this to decide
   * whether to show its import-wait home. Defaults to `true` when unknown —
   * an unknown state must never render "still importing".
   */
  athenaFinished: boolean;
}

// IMPORTANT: a real StoreModel's `isAthenaFinished` is declared `boolean` but
// is actually `boolean | undefined` at runtime. The constructor (store.ts)
// reads it from the server payload, but `mapData()` — the only mapper the
// live store ever runs through — never assigns the field at all, so a
// fresh/loading store holds `undefined`. `checkEtlStatus()` only ever
// assigns `= true` once the import has actually finished; the field is never
// explicitly set back to `false` on the happy path (the lone `= false` is in
// an error catch). So `undefined` is the store's real "still importing"
// state, and it is indistinguishable from "store hasn't loaded yet" by the
// field alone — both are `undefined`. We disambiguate using store identity
// (`store.id`) instead: an unloaded/missing store still fails safe to
// `true` (never claim "still importing" when we don't actually know), but a
// LOADED store with an undefined field means "loaded, still importing" and
// must report `false`. Do not simplify this back to `store?.isAthenaFinished
// !== false` — that collapses both states to `true` and makes the
// import-wait home undead-on-arrival (US-TODAY-101 review Critical-1).
export function buildAtlasContext(store: StoreModel | null | undefined): AtlasContext {
  const storeLoaded = store?.id != null;
  return {
    packageName: store?.currentPackage?.name ?? null,
    athenaFinished: storeLoaded ? !!store!.isAthenaFinished : true,
  };
}

/**
 * `/logout` is NOT a route. It is a sentinel string in the menu configs
 * (`src/config/menu/*.ts`) that VerticalMenuBar.handleMenuClick intercepts in
 * JavaScript and never navigates to:
 *
 *   handleMenuClick -> if (link === '/logout') this.doLogout()
 *                   -> $emit('logout') -> App.doLogout()
 *
 * Atlas's settings sidebar (SettingsSidebar.tsx) copied the string into a
 * `parentPath`, so a click arrives here as an ordinary navigation request.
 * Pushing it at the router falls through to `path: '*'` -> `redirect: '/'`
 * (router/index.ts): the user lands on Home believing they logged out while the
 * session is fully intact. AS-10880.
 */
export const ATLAS_LOGOUT_SENTINEL = '/logout';

export type AtlasParentNavigateIntent =
  | { kind: 'logout' }
  | { kind: 'navigate'; path: string };

/**
 * Decide what a PARENT_NAVIGATE path from Atlas actually means.
 *
 * Lives here rather than in AtlasEmbed.vue because no branch pipeline
 * type-checks a `.vue`, so logic left in the component is effectively untested.
 *
 * The sentinel match is EXACT on the normalized path — a query string, a hash
 * and trailing slashes are tolerated, nothing else. A prefix or substring
 * match would turn any future route whose name merely contains "logout"
 * (`/logout-report`, `/settings/logout`) into a session-destroying link.
 *
 * `navigate` carries the path back VERBATIM: normalization exists only to
 * compare against the sentinel, never to rewrite a real navigation.
 */
export function classifyAtlasParentNavigate(path: string): AtlasParentNavigateIntent {
  const rawPath = path ?? '';
  const withoutQueryOrHash = rawPath.split('?')[0].split('#')[0];
  const normalized = withoutQueryOrHash.replace(/\/+$/, '') || withoutQueryOrHash;

  if (normalized === ATLAS_LOGOUT_SENTINEL) {
    return { kind: 'logout' };
  }
  return { kind: 'navigate', path: rawPath };
}
